import { useCallback, useEffect, useState } from 'react'

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message)
    this.status = status
    this.code = code
    this.details = details
  }
}

export async function api(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(path, {
    method,
    signal,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (res.status === 204) return null
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    // Where there's no form to point at (chat, say), the field's own message
    // (the word filter's, for one) says more than "Check the highlighted fields."
    const fieldMessage = data?.error?.code === 'invalid' ? Object.values(data.error.details?.fieldErrors ?? {})[0] : null
    throw new ApiError(
      res.status,
      data?.error?.code ?? 'error',
      fieldMessage ?? data?.error?.message ?? `Request failed (${res.status})`,
      data?.error?.details,
    )
  }
  return data
}

// Fetches JSON for a component. With refreshMs it refetches on an interval
// while the tab is visible, keeping the last good data if a refresh fails.
export function useApi(path, { refreshMs } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: true })
  // Each new request object triggers one fetch; `n` bumps it for reloads.
  const [request, setRequest] = useState({ path, n: 0 })
  if (request.path !== path) setRequest({ path, n: 0 })

  useEffect(() => {
    // A null path waits (e.g. until there's someone signed in).
    if (!request.path) return undefined
    const controller = new AbortController()
    const run = async () => {
      try {
        const data = await api(request.path, { signal: controller.signal })
        setState({ data, error: null, loading: false })
      } catch (error) {
        if (error.name !== 'AbortError') setState((s) => ({ ...s, error, loading: false }))
      }
    }
    run()
    return () => controller.abort()
  }, [request])

  useEffect(() => {
    if (!refreshMs) return undefined
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') setRequest((r) => ({ ...r, n: r.n + 1 }))
    }, refreshMs)
    return () => clearInterval(timer)
  }, [refreshMs])

  const reload = useCallback(() => {
    setState((s) => ({ ...s, loading: true }))
    setRequest((r) => ({ ...r, n: r.n + 1 }))
  }, [])

  return { ...state, reload }
}
